/**
 * GST e-Invoice JSON builder (Schema Version 1.1).
 *
 * Maps invoice tracker / create-invoice row fields into the official
 * TranDtls / DocDtls / SellerDtls / BuyerDtls / ValDtls / ItemList shape
 * used for NIC e-invoice JSON upload.
 */

const DEFAULT_SELLER = {
  Gstin: '27ABDCS0586N1ZT',
  LglNm: 'SATTVA HUMAN PRIVATE LIMITED',
  TrdNm: 'SATTVA HUMAN PRIVATE LIMITED',
  Addr1: 'OFFICE 1, 1ST FLOOR, RUIA CHAMBERS, SECTOR C-2, AMANORA PARK TOWN, HADAPSAR',
  Loc: 'PUNE',
  Pin: 411028,
  Stcd: '27',
}

const DEFAULT_HSN = '998512'
const DEFAULT_GST_RATE = 18

function pick(obj, keys, fallback = null) {
  if (!obj) return fallback
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]]
    if (v != null && v !== '') return v
  }
  return fallback
}

function asNumber(value, fallback = 0) {
  if (value == null || value === '') return fallback
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''))
  return Number.isFinite(n) ? n : fallback
}

/** Round to 2 decimals as a Number (sample uses numeric amounts, not strings). */
function round2(value) {
  const n = asNumber(value, 0)
  return Math.round(n * 100) / 100
}

function nullIfEmpty(value) {
  if (value == null) return null
  const s = String(value).trim()
  return s === '' ? null : s
}

function upperOrNull(value) {
  const s = nullIfEmpty(value)
  return s == null ? null : s.toUpperCase()
}

function stateCodeFromGstin(gstin) {
  const m = /^(\d{2})/.exec(String(gstin || '').trim())
  return m ? m[1] : null
}

function extractPin(address, explicitPin) {
  if (explicitPin != null && explicitPin !== '') {
    const n = parseInt(String(explicitPin).replace(/\D/g, ''), 10)
    if (Number.isFinite(n) && String(n).length === 6) return n
  }
  const m = /\b(\d{6})\b/.exec(String(address || ''))
  return m ? parseInt(m[1], 10) : null
}

function splitAddress(address) {
  const raw = nullIfEmpty(address)
  if (!raw) return { Addr1: null, Addr2: null }
  const parts = raw.split(/[\n,]/).map(p => p.trim()).filter(Boolean)
  if (parts.length <= 1) return { Addr1: raw, Addr2: null }
  // Keep full address on Addr1 (sample style); Addr2 only when a clear 2nd line exists via newline
  if (raw.includes('\n')) {
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean)
    return {
      Addr1: lines[0] || raw,
      Addr2: lines.slice(1).join(', ') || null,
    }
  }
  return { Addr1: raw, Addr2: null }
}

/**
 * DocDtls.Dt → DD/MM/YYYY
 */
export function formatEInvoiceDate(date) {
  if (date == null || date === '') return null
  const s = String(date).trim()
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s

  const dmy = s.match(/^(\d{2})[-.](\d{2})[-.](\d{4})$/)
  if (dmy) return `${dmy[1]}/${dmy[2]}/${dmy[3]}`

  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`

  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    return `${day}/${month}/${d.getFullYear()}`
  }
  return s
}

/**
 * DocDtls.Typ:
 * - CRN — CreditNote / credit note / revised credit note
 * - DBN — DebitNote / debit note
 * - INV — everything else (tax invoice, raised, issued, revised invoice, …)
 */
export function resolveEInvoiceDocTyp(invoice = {}) {
  const status = String(pick(invoice, ['status'], '') || '')
  const docType = String(pick(invoice, ['document_type', 'documentType', 'doc_type', 'docType'], '') || '')
  const invType = String(pick(invoice, ['invoice_type', 'invoiceType', 'type'], '') || '')
  const noteType = String(pick(invoice, ['note_type', 'noteType', 'revision_type', 'revisionType'], '') || '')
  const no = String(pick(invoice, ['invoice_id', 'invoiceId', 'invoice_number', 'invoiceNumber'], '') || '')

  const hay = `${status} ${docType} ${invType} ${noteType}`.toLowerCase().replace(/[_-]+/g, ' ')

  if (/\bdebit\s*note\b|\bdbn\b/.test(hay)) return 'DBN'
  if (
    /\bcredit\s*note\b|\bcreditnote\b|\brevised\s*credit\b|\bcrn\b/.test(hay)
    || /^creditnote$/i.test(status.replace(/[\s_-]/g, ''))
  ) {
    return 'CRN'
  }
  // Common Sattva credit-note number prefixes when status wasn't updated
  if (/^CN\d/i.test(no.trim()) || /^CN[-_/]/i.test(no.trim())) return 'CRN'
  if (/^DN\d/i.test(no.trim()) || /^DN[-_/]/i.test(no.trim())) return 'DBN'
  return 'INV'
}

export function resolveTaxBreakdown(invoice = {}) {
  let cgst = asNumber(pick(invoice, ['cgst_amount', 'cgstAmount', 'cgst', 'CgstVal'], 0))
  let sgst = asNumber(pick(invoice, ['sgst_amount', 'sgstAmount', 'sgst', 'SgstVal'], 0))
  let igst = asNumber(pick(invoice, ['igst_amount', 'igstAmount', 'igst', 'IgstVal'], 0))
  const gst = asNumber(pick(invoice, ['gst_amount', 'gstAmount', 'gst'], 0))
  const gstType = String(pick(invoice, ['gst_type', 'gstType'], '') || '').toLowerCase()

  if (cgst === 0 && sgst === 0 && igst === 0 && gst > 0) {
    if (gstType.includes('igst')) {
      igst = gst
    } else {
      cgst = gst / 2
      sgst = gst / 2
    }
  }

  return {
    cgst: round2(cgst),
    sgst: round2(sgst),
    igst: round2(igst),
  }
}

function resolveGstRate(assVal, tax, explicitRate) {
  if (explicitRate != null && explicitRate !== '') {
    const r = asNumber(explicitRate, NaN)
    if (Number.isFinite(r) && r > 0) return round2(r)
  }
  if (assVal > 0) {
    if (tax.igst > 0) return round2((tax.igst / assVal) * 100)
    if (tax.cgst > 0 || tax.sgst > 0) return round2(((tax.cgst + tax.sgst) / assVal) * 100)
  }
  return DEFAULT_GST_RATE
}

function parseMaybeJson(value) {
  if (value == null) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return null
  const s = value.trim()
  if (!s || (s[0] !== '{' && s[0] !== '[')) return null
  try {
    return JSON.parse(s)
  } catch (e) {
    return null
  }
}

export function collectLineItems(invoice = {}) {
  const meta = parseMaybeJson(invoice.metadata) || invoice.metadata || {}
  const candidates = [
    invoice.line_items,
    invoice.lineItems,
    invoice.items,
    invoice.ItemList,
    meta.line_items,
    meta.lineItems,
    meta.items,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const raw = typeof candidates[i] === 'string' ? parseMaybeJson(candidates[i]) : candidates[i]
    if (Array.isArray(raw) && raw.length > 0) return raw
  }
  const jsonStr = pick(invoice, ['line_items_json', 'lineItemsJson'], null)
  const parsed = parseMaybeJson(jsonStr)
  if (Array.isArray(parsed) && parsed.length > 0) return parsed
  return []
}

function mapLineItem(item, index, fallbackTax, fallbackAss, fallbackTotal, fallbackHsn, fallbackRate) {
  const qty = asNumber(pick(item, ['quantity', 'qty', 'Qty'], 1), 1) || 1
  const ass = round2(pick(item, [
    'assAmt', 'AssAmt', 'taxable_amount', 'taxableAmount', 'baseAmount', 'base_amount',
    'amount', 'Amount', 'unitPrice', 'unit_price', 'TotAmt',
  ], fallbackAss))
  const unitPrice = round2(pick(item, ['unitPrice', 'unit_price', 'UnitPrice', 'rate', 'Rate'], ass / qty || ass))
  const cgst = round2(pick(item, ['cgstAmount', 'cgst_amount', 'CgstAmt', 'cgst'], index === 0 ? fallbackTax.cgst : 0))
  const sgst = round2(pick(item, ['sgstAmount', 'sgst_amount', 'SgstAmt', 'sgst'], index === 0 ? fallbackTax.sgst : 0))
  const igst = round2(pick(item, ['igstAmount', 'igst_amount', 'IgstAmt', 'igst'], index === 0 ? fallbackTax.igst : 0))
  const totItem = round2(pick(item, [
    'totalAmountWithGST', 'total_amount_with_gst', 'TotItemVal', 'totalAmount', 'total_amount',
  ], ass + cgst + sgst + igst || fallbackTotal))
  const gstRt = resolveGstRate(ass, { cgst, sgst, igst }, pick(item, ['gstRate', 'gst_rate', 'GstRt'], fallbackRate))

  return {
    SlNo: String(pick(item, ['slNo', 'SlNo', 'srNo', 'sr_no'], index + 1)),
    PrdDesc: String(pick(item, [
      'description', 'PrdDesc', 'itemName', 'item_name', 'positionName', 'particulars', 'name',
    ], 'Manpower Supply') || 'Manpower Supply'),
    IsServc: 'Y',
    HsnCd: String(pick(item, ['hsnCd', 'HsnCd', 'sacCode', 'sac_code', 'hsn', 'hsnCode', 'hsn_code'], fallbackHsn) || fallbackHsn),
    Barcde: null,
    Qty: round2(qty),
    FreeQty: 0,
    Unit: String(pick(item, ['unit', 'Unit', 'uom'], 'OTH') || 'OTH'),
    UnitPrice: unitPrice,
    TotAmt: ass,
    Discount: round2(pick(item, ['discount', 'Discount'], 0)),
    PreTaxVal: 0,
    AssAmt: ass,
    GstRt: gstRt,
    IgstAmt: igst,
    CgstAmt: cgst,
    SgstAmt: sgst,
    CesRt: 0,
    CesAmt: 0,
    CesNonAdvlAmt: 0,
    StateCesRt: 0,
    StateCesAmt: 0,
    StateCesNonAdvlAmt: 0,
    OthChrg: 0,
    TotItemVal: totItem,
    BchDtls: null,
  }
}

function buildSyntheticItem(invoice, tax, assVal, totInvVal, hsn, gstRt) {
  const desc = pick(invoice, [
    'description', 'PrdDesc', 'candidate_name', 'candidateName', 'category',
  ], null)
  let prdDesc = nullIfEmpty(desc)
  if (!prdDesc) {
    const candidate = nullIfEmpty(pick(invoice, ['candidate_name', 'candidateName'], null))
    prdDesc = candidate
      ? `Placement charges of ${candidate}`
      : 'Contractual Billing for Manpower Supply'
  }

  return {
    SlNo: '1',
    PrdDesc: prdDesc,
    IsServc: 'Y',
    HsnCd: hsn,
    Barcde: null,
    Qty: 1,
    FreeQty: 0,
    Unit: 'OTH',
    UnitPrice: assVal,
    TotAmt: assVal,
    Discount: 0,
    PreTaxVal: 0,
    AssAmt: assVal,
    GstRt: gstRt,
    IgstAmt: tax.igst,
    CgstAmt: tax.cgst,
    SgstAmt: tax.sgst,
    CesRt: 0,
    CesAmt: 0,
    CesNonAdvlAmt: 0,
    StateCesRt: 0,
    StateCesAmt: 0,
    StateCesNonAdvlAmt: 0,
    OthChrg: 0,
    TotItemVal: totInvVal,
    BchDtls: null,
  }
}

function buildSellerDtls(invoice = {}) {
  const gstin = upperOrNull(pick(invoice, [
    'sattva_gst', 'sattvaGst', 'sattvaGST', 'seller_gstin', 'sellerGstin', 'Gstin',
  ], DEFAULT_SELLER.Gstin)) || DEFAULT_SELLER.Gstin

  const name = upperOrNull(pick(invoice, [
    'sattva_company_name', 'sattvaCompanyName', 'seller_name', 'sellerName', 'LglNm',
  ], DEFAULT_SELLER.LglNm)) || DEFAULT_SELLER.LglNm

  const address = pick(invoice, [
    'sattva_address', 'sattvaAddress', 'seller_address', 'sellerAddress', 'Addr1',
  ], DEFAULT_SELLER.Addr1)
  const { Addr1, Addr2 } = splitAddress(address)

  const loc = upperOrNull(pick(invoice, [
    'sattva_location', 'sattvaLocation', 'location', 'seller_city', 'sellerCity', 'Loc',
  ], DEFAULT_SELLER.Loc)) || DEFAULT_SELLER.Loc

  const stcd = stateCodeFromGstin(gstin)
    || nullIfEmpty(pick(invoice, ['sattva_state_code', 'sattvaStateCode', 'Stcd'], null))
    || DEFAULT_SELLER.Stcd

  const pin = extractPin(address, pick(invoice, ['sattva_pin', 'sattvaPIN', 'sattva_pincode', 'Pin'], null))
    || DEFAULT_SELLER.Pin

  return {
    Gstin: gstin,
    LglNm: name,
    TrdNm: name,
    Addr1: Addr1 || DEFAULT_SELLER.Addr1,
    Addr2,
    Loc: loc,
    Pin: pin,
    Stcd: String(stcd),
    Ph: nullIfEmpty(pick(invoice, ['sattva_phone', 'sattvaPhone', 'Ph'], null)),
    Em: nullIfEmpty(pick(invoice, ['sattva_email', 'sattvaEmail', 'Em'], null)),
  }
}

function buildBuyerDtls(invoice = {}) {
  const gstin = upperOrNull(pick(invoice, [
    'client_gst', 'clientGst', 'clientGST', 'client_gstin', 'clientGstin', 'buyer_gstin', 'buyerGstin',
  ], null))

  const name = nullIfEmpty(pick(invoice, [
    'client_legal_name', 'clientLegalName', 'legal_name', 'legalName',
    'client_name', 'clientName', 'client', 'LglNm',
  ], null))

  const address = pick(invoice, [
    'client_address', 'clientAddress', 'einvoice_address', 'einvoiceAddress',
    'buyer_address', 'buyerAddress', 'Addr1',
  ], null)
  const { Addr1, Addr2 } = splitAddress(address)

  const stcd = stateCodeFromGstin(gstin)
    || nullIfEmpty(pick(invoice, ['client_state_code', 'clientStateCode', 'Stcd'], null))

  const posRaw = pick(invoice, [
    'place_of_supply', 'placeOfSupply', 'place_of_supply_state_code', 'placeOfSupplyStateCode', 'Pos',
  ], null)
  let pos = nullIfEmpty(posRaw)
  if (pos && /^\d{2}$/.test(pos)) {
    // already state code
  } else if (pos) {
    // place name — prefer GSTIN state code for Pos (sample uses "27"/"29")
    pos = stcd || pos
  } else {
    pos = stcd
  }

  const loc = nullIfEmpty(pick(invoice, [
    'client_city', 'clientCity', 'client_state', 'clientState', 'Loc',
  ], null))

  const pin = extractPin(address, pick(invoice, ['client_pin', 'clientPIN', 'client_pincode', 'Pin'], null))

  return {
    Gstin: gstin,
    LglNm: name,
    TrdNm: name,
    Pos: pos != null ? String(pos) : null,
    Addr1,
    Addr2,
    Loc: loc,
    Pin: pin,
    Stcd: stcd != null ? String(stcd) : null,
    Ph: nullIfEmpty(pick(invoice, ['client_phone', 'clientPhone', 'Ph'], null)),
    Em: nullIfEmpty(pick(invoice, ['client_email', 'clientEmail', 'Em'], null)),
  }
}

/**
 * Build one Version 1.1 e-invoice document from an invoice row / form object.
 * @param {object} invoice
 * @returns {object}
 */
export function buildEInvoiceDocument(invoice = {}) {
  const tax = resolveTaxBreakdown(invoice)
  const assVal = round2(pick(invoice, [
    'base_amount', 'baseAmount', 'taxable_value', 'taxableValue', 'AssVal',
  ], 0))
  const totInvVal = round2(pick(invoice, [
    'total_amount', 'totalAmount', 'amount_with_gst', 'amountWithGst', 'TotInvVal',
  ], assVal + tax.cgst + tax.sgst + tax.igst))

  const hsn = String(pick(invoice, [
    'sattva_sac_code', 'sattvaSacCode', 'sac_code', 'sacCode', 'hsn', 'hsn_code', 'hsnCode',
  ], DEFAULT_HSN) || DEFAULT_HSN)

  const gstRt = resolveGstRate(
    assVal,
    tax,
    pick(invoice, ['gst_rate', 'gstRate'], null),
  )

  const lines = collectLineItems(invoice)
  const itemList = lines.length > 0
    ? lines.map((item, idx) => mapLineItem(item, idx, tax, assVal, totInvVal, hsn, gstRt))
    : [buildSyntheticItem(invoice, tax, assVal, totInvVal, hsn, gstRt)]

  // If multiple line items lacked per-line tax, ensure ValDtls still matches header tax
  const docNo = String(pick(invoice, [
    'invoice_id', 'invoiceId', 'invoice_number', 'invoiceNumber', 'No',
  ], '') || '')

  return {
    Version: '1.1',
    TranDtls: {
      TaxSch: 'GST',
      SupTyp: 'B2B',
      IgstOnIntra: 'N',
      RegRev: null,
      EcmGstin: null,
    },
    DocDtls: {
      Typ: resolveEInvoiceDocTyp(invoice),
      No: docNo,
      Dt: formatEInvoiceDate(pick(invoice, ['invoice_date', 'invoiceDate', 'Dt'], null)),
    },
    SellerDtls: buildSellerDtls(invoice),
    BuyerDtls: buildBuyerDtls(invoice),
    DispDtls: null,
    ShipDtls: null,
    ValDtls: {
      AssVal: assVal,
      IgstVal: tax.igst,
      CgstVal: tax.cgst,
      SgstVal: tax.sgst,
      CesVal: 0,
      StCesVal: 0,
      Discount: 0,
      OthChrg: 0,
      RndOffAmt: round2(pick(invoice, ['round_off', 'roundOff', 'RndOffAmt'], 0)),
      TotInvVal: totInvVal,
    },
    ExpDtls: {
      ShipBNo: null,
      ShipBDt: null,
      Port: null,
      RefClm: null,
      ForCur: null,
      CntCode: null,
      ExpDuty: 0,
    },
    EwbDtls: null,
    ItemList: itemList,
  }
}

/**
 * Build export payload: single document for one invoice, array for many.
 * @param {object|object[]} invoiceOrList
 * @returns {object|object[]}
 */
export function buildEInvoiceExport(invoiceOrList) {
  if (Array.isArray(invoiceOrList)) {
    return invoiceOrList.map(inv => buildEInvoiceDocument(inv || {}))
  }
  return buildEInvoiceDocument(invoiceOrList || {})
}

export default {
  buildEInvoiceDocument,
  buildEInvoiceExport,
  collectLineItems,
  formatEInvoiceDate,
  resolveEInvoiceDocTyp,
  resolveTaxBreakdown,
  DEFAULT_SELLER,
  DEFAULT_HSN,
}
